import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getStudentById, updateStudent } from "../../services/StudentService";
import { HSTSToast } from "../../utils/hstsToast";
import { ToastContainer } from "react-toastify";

export const UpdateStudent = () => {
  const navigator = useNavigate();
  const { id } = useParams();

  const [fullName, setFullName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [gender, setGender] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [ageGroup, setAgeGroup] = useState("");

  const [errors, setErrors] = useState({
    fullName: "",
    phoneNumber: "",
    gender: "",
    emailAddress: "",
    ageGroup: "",
  });

  const [touched, setTouched] = useState({
    fullName: false,
    phoneNumber: false,
    gender: false,
    emailAddress: false,
    ageGroup: false,
  });

  const isValidPhoneNumber = (number) => {
    const localPattern = /^(06|07)\d{8}$/;
    const intlPattern = /^\+255\s[67]\d{2}\s\d{3}\s\d{3}$/;
    return localPattern.test(number) || intlPattern.test(number);
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const formValidation = () => {
    let valid = true;
    const copy = { ...errors };

    if (!fullName.trim()) {
      copy.fullName = "Full name is required.";
      valid = false;
    } else copy.fullName = "";

    if (!phoneNumber.trim()) {
      copy.phoneNumber = "Phone number is required.";
      valid = false;
    } else if (!isValidPhoneNumber(phoneNumber.trim())) {
      copy.phoneNumber =
        "Invalid phone format. Use 0769xxxxxx or +255 769 xxx xxx.";
      valid = false;
    } else copy.phoneNumber = "";

    if (!gender.trim()) {
      copy.gender = "Gender is required.";
      valid = false;
    } else copy.gender = "";

    if (!ageGroup.trim()) {
      copy.ageGroup = "Age group is required.";
      valid = false;
    } else copy.ageGroup = "";

    if (!emailAddress.trim()) {
      copy.emailAddress = "Email is required.";
      valid = false;
    } else if (!validateEmail(emailAddress)) {
      copy.emailAddress = "Invalid email address.";
      valid = false;
    } else copy.emailAddress = "";

    setErrors(copy);
    return valid;
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    formValidation();
  };

  const getInputClass = (fieldName, value) => {
    if (!touched[fieldName]) return "form-control";
    if (errors[fieldName]) return "form-control is-invalid";
    if (value.trim()) return "form-control is-valid";
    return "form-control";
  };

  const saveUpdatedStudent = (e) => {
    e.preventDefault();
    setTouched({
      fullName: true,
      phoneNumber: true,
      gender: true,
      emailAddress: true,
      ageGroup: true,
    });

    if (formValidation()) {
      const updated = { fullName, phoneNumber, gender, ageGroup, emailAddress };

      updateStudent(id, updated)
        .then(() => {
          HSTSToast("success", "Student successfully updated");
          setTimeout(() => navigator("/coordinator/student"), 1200);
        })
        .catch((err) => console.error(err));
    }
  };

  useEffect(() => {
    if (id) {
      getStudentById(id)
        .then((res) => {
          const s = res.data;
          setFullName(s.fullName);
          setPhoneNumber(s.phoneNumber);
          setGender(s.gender);
          setAgeGroup(s.ageGroup);
          setEmailAddress(s.emailAddress);
        })
        .catch((err) => console.error("Error fetching student:", err));
    }
  }, [id]);

  return (
    <>
      <div className="row">
        <div className="col-12 col-md-8  col-lg-4">
          <div className="card-body">
            <h5 className="mb-3">
              <span className="fas fa-user-graduate text-secondary"></span> Change student informations
            </h5>
            <form onSubmit={saveUpdatedStudent}>
              <div className="form-group mb-2">
                <label className="text-black">Email Address</label>
                <input
                  type="email"
                  className={getInputClass("emailAddress", emailAddress)}
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  onBlur={() => handleBlur("emailAddress")}
                  placeholder="Enter email"
                  disabled
                  style={{ color: "#000", fontWeight: "bold" }}
                />
                {errors.emailAddress && (
                  <div className="invalid-feedback">{errors.emailAddress}</div>
                )}
              </div>

              <div className="form-group mb-2">
                <label className="text-black">Full Name</label>
                <input
                  type="text"
                  className={getInputClass("fullName", fullName)}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  onBlur={() => handleBlur("fullName")}
                  placeholder="Enter full name"
                />
                {errors.fullName && (
                  <div className="invalid-feedback">{errors.fullName}</div>
                )}
              </div>

              <div className="form-group mb-2">
                <label className="text-black">Phone Number</label>
                <input
                  type="tel"
                  className={getInputClass("phoneNumber", phoneNumber)}
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  onBlur={() => handleBlur("phoneNumber")}
                  placeholder="Enter phone number"
                />
                {errors.phoneNumber && (
                  <div className="invalid-feedback">{errors.phoneNumber}</div>
                )}
              </div>

              <div className="form-group mb-2">
                <label className="text-black">Age group</label>
                <select
                  className={
                    touched.ageGroup
                      ? errors.ageGroup
                        ? "form-select is-invalid"
                        : "form-select is-valid"
                      : "form-select"
                  }
                  value={ageGroup}
                  onChange={(e) => setAgeGroup(e.target.value)}
                  onBlur={() => handleBlur("ageGroup")}
                >
                  <option value="">Select ageGroup</option>
                  <option value="18 – 27">18 – 27</option>
                  <option value="28 – 37">28 – 37</option>
                  <option value="38 – 47">38 – 47</option>
                  <option value="48 – Above">48 – Above</option>
                </select>
                {errors.ageGroup && (
                  <div className="invalid-feedback">{errors.ageGroup}</div>
                )}
              </div>

              <div className="form-group mb-2">
                <label className="text-black">Gender</label>
                <select
                  className={
                    touched.gender
                      ? errors.gender
                        ? "form-select is-invalid"
                        : "form-select is-valid"
                      : "form-select"
                  }
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  onBlur={() => handleBlur("gender")}
                >
                  <option value="">Select Gender</option>
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                </select>
                {errors.gender && (
                  <div className="invalid-feedback">{errors.gender}</div>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-black w-100 rounded-3 mb-4"
              >
                Change
              </button>
            </form>
          </div>
        </div>
      </div>

      <ToastContainer />
    </>
  );
};
